import { PayloadAction } from '@reduxjs/toolkit';
import createAppSlice from 'app/createAppSlice';
import sortQuestions from 'utils/sortQuestions';
import type {
  IdauthVerifyState,
  IdauthScreen,
  OutcomeCode,
  DitResult,
  IdauthQuestion,
} from '../types';

const initialState: IdauthVerifyState = {
  currentScreen: 'INITIAL_CHECK',
  ditChecked: false,
  phoneTrust: false,
  emailTrust: false,
  questions: [],
  currentIndex: 0,
  collectedAnswers: {},
  questionnaireId: null,
  outcomeCode: null,
  pendingLogout: false,
  hoursUntilRetry: null,
};

const idauthVerifySlice = createAppSlice({
  name: 'idauthVerify',
  initialState,
  reducers: {
    resetIdauthState() {
      return initialState;
    },

    setScreen(state, action: PayloadAction<IdauthScreen>) {
      state.currentScreen = action.payload;
    },

    setDitResult(state, action: PayloadAction<DitResult>) {
      const { verified, phoneTrust, emailTrust } = action.payload;
      state.ditChecked = true;
      if (!verified) {
        state.pendingLogout = true;
        state.outcomeCode = 'DIT_DENY';
        state.currentScreen = 'OUTCOME';
        return;
      }
      state.phoneTrust = phoneTrust;
      state.emailTrust = emailTrust;
      state.currentScreen = 'INITIAL_PROMPT';
    },

    setQuestions(state, action: PayloadAction<IdauthQuestion[]>) {
      const sorted = sortQuestions(action.payload, (item) =>
        parseFloat((item as IdauthQuestion).id),
      );
      state.questions = sorted;
      state.currentIndex = 0;
      state.collectedAnswers = {};
      if (sorted.length > 0) {
        state.questionnaireId = sorted[0].questionnaireId;
      }
    },

    recordAnswer(
      state,
      action: PayloadAction<{ questionId: string; answerId: string }>,
    ) {
      const { questionId, answerId } = action.payload;
      state.collectedAnswers[questionId] = answerId;
    },

    setCurrentIndex(state, action: PayloadAction<number>) {
      state.currentIndex = action.payload;
    },

    setOutcome(
      state,
      action: PayloadAction<{ code: OutcomeCode; pendingLogout?: boolean }>,
    ) {
      state.outcomeCode = action.payload.code;
      state.currentScreen = 'OUTCOME';
      if (action.payload.pendingLogout) {
        state.pendingLogout = true;
      }
    },

    setHoursUntilRetry(state, action: PayloadAction<number>) {
      state.hoursUntilRetry = action.payload;
    },

    // Retry path after QUIZ_INCORRECT_ANSWER — keep cached questions (BR-009)
    resetQuizForRetry(state) {
      state.collectedAnswers = {};
      state.currentIndex = 0;
      state.outcomeCode = null;
      state.currentScreen = 'METHOD_SELECT';
    },

    clearPendingLogout(state) {
      state.pendingLogout = false;
    },
  },
});

export const {
  resetIdauthState,
  setScreen,
  setDitResult,
  setQuestions,
  recordAnswer,
  setCurrentIndex,
  setOutcome,
  setHoursUntilRetry,
  resetQuizForRetry,
  clearPendingLogout,
} = idauthVerifySlice.actions;

export default idauthVerifySlice.reducer;
